import { AppImageDimension, AppImages } from "app/appImages";
import { CustomVideoSlideRender } from "components/CustomVideoSlideRender";
import { CustomVideoSlide, CustomVideoSlideEvents } from "components/types/lightBox";
import { FeedAttachDto, PublicationAttachType, PublicationPresentation } from "generated/CoreApi_v1";
import { useCallback, useRef } from "react";
import { FeedAttachHelper } from "views/knowledge/components/card/FeedItemHelper";
import Lightbox, { ContainerRect, Render, Slide, SlideImage } from "yet-another-react-lightbox";
import Captions from "yet-another-react-lightbox/plugins/captions";
import "yet-another-react-lightbox/plugins/captions.css";
import Fullscreen from "yet-another-react-lightbox/plugins/fullscreen";
import Slideshow from "yet-another-react-lightbox/plugins/slideshow";
import Thumbnails from "yet-another-react-lightbox/plugins/thumbnails";
import "yet-another-react-lightbox/plugins/thumbnails.css";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import "yet-another-react-lightbox/styles.css";

////////////////////////////////////////////////////////////////////////////////

export function BuildLighboxSlide(at: FeedAttachDto, autoPlay: boolean, videoEvents?: CustomVideoSlideEvents) {
    const a = new FeedAttachHelper(at);
    const asset = a.getAsset();
    const thumb = a.getThumbnail();


    if (a.useMediaPlayer()) {
        const slide: CustomVideoSlide = {
            type: "custom-video-slide",
            id: at.attachId,
            videoId: at.fileId!,
            url: asset.url,
            width: asset.width,
            height: asset.height,
            duration: asset.duration,
            mimetype: at?.fileMimeType,
            mediaSessionTitle: a.getTitle(),
            poster: a.getPosterUrl(),
            thumbnailUrl: thumb.url,
            isAudio: a.isAudio(),
            isHls: a.isHls(),
            autoPlay: autoPlay,
            title: a.getTitle(),
            description: a.getDescription(),
            tracks: asset.mediaTracks,
            ...videoEvents
        };

        return slide;
    }

    if (at.attachType == PublicationAttachType.Video && !a.useMediaPlayer()) {
        const posterUrl = a.getPosterUrl()!;
        const thumbnail = a.getThumbnail();

        const slide: SlideImage = {
            type: "image",
            title: a.getTitle(),
            description: a.getDescription(),
            src: posterUrl,
            height: asset.height,
            width: asset.width,
            imageFit: asset.width < asset.height ? "contain" : "cover",
            srcSet: [
                { src: posterUrl, width: AppImageDimension.CardPicture.width, height: AppImageDimension.CardPicture.height },
                { src: thumbnail.url, height: thumbnail.height, width: thumbnail.width }
            ]
        };

        return slide;
    }

    if (at.attachType == PublicationAttachType.Image) {
        const slide: SlideImage = {
            type: "image",
            title: a.getTitle(),
            description: a.getDescription(),
            src: asset.url!,
            height: asset.height,
            width: asset.width,
            imageFit: asset.width < asset.height ? "contain" : "cover",
            srcSet: [
                { src: asset.url, width: asset.width, height: asset.height },
                { src: thumb.url, width: thumb.width!, height: thumb.height! }
            ]
        };

        return slide;
    }

    return null;
}

export const BuildLigthBoxSlides = (presentation: PublicationPresentation, attachs: FeedAttachDto[], autoplay: boolean, videoEvents?: CustomVideoSlideEvents) => {
    const slides: Array<CustomVideoSlide | SlideImage> = [];
    if (presentation) {
        // Prepara Slides.
        for (let i = 0; i < presentation.orderedItems.length; i++) {
            var at = attachs?.find(p => p.attachId == presentation.orderedItems[i]);
            if (at) {
                const slide = BuildLighboxSlide(at, autoplay, videoEvents);
                if (slide != null) slides.push(slide);
            }
        }
    }
    return slides;
};


export interface CardLightBoxProps {
    open: boolean;
    index?: number;
    slides: Array<CustomVideoSlide | SlideImage>;
    autoplay: boolean;
    enterFullScreen?: boolean;
    rotationInterval: number;
    onClose: () => void;
}

export const CardLightBox = ({ open, index, slides, autoplay, rotationInterval, enterFullScreen = false, onClose }: CardLightBoxProps) => {
    const count = slides.length;
    const plugins = [Captions, Fullscreen, Zoom];

    const isVideo = useRef<boolean>(false);
    const zoomIn = useRef<HTMLElement | undefined>(undefined);
    const zoomOut = useRef<HTMLElement | undefined>(undefined);
    const fullScreen = useRef<HTMLElement | undefined>(undefined);
    const slideshowRef = useRef<any>(null);

    if (count > 1) {
        plugins.push(Thumbnails);
        plugins.push(Slideshow);
    }

    const onPlaying = useCallback((playing: boolean) => {
        if (count > 1) {
            if (playing) {
                if (slideshowRef.current?.disabled === false) slideshowRef.current?.pause();
            }
            else {
                if (autoplay) slideshowRef.current?.play();
            }
        }
    }, []);

    const checkShowZoom = () => {
        if (zoomIn.current) zoomIn.current.style.display = isVideo.current ? "none" : "";
        if (zoomOut.current) zoomOut.current.style.display = isVideo.current ? "none" : "";
        if (fullScreen.current) fullScreen.current.style.display = isVideo.current ? "none" : "";
    };

    return (
        <Lightbox
            open={open}
            close={onClose}
            index={index}
            slides={slides}
            on={{
                entered() {
                    const els = Array.from(document.getElementsByClassName("yarl__button"));
                    var el1 = els.find(p => p.getAttribute("aria-label") == "Zoom in");
                    var el2 = els.find(p => p.getAttribute("aria-label") == "Zoom out");
                    var el3 = els.find(p => p.getAttribute("aria-label") == "Enter Fullscreen");
                    if (el1) zoomIn.current = (el1 as HTMLElement);
                    if (el2) zoomOut.current = (el2 as HTMLElement);
                    if (el3) fullScreen.current = (el3 as HTMLElement);
                    checkShowZoom();
                },
                view({ index }) {
                    isVideo.current = ("type" in slides[index] && slides[index].type === "custom-video-slide");
                    checkShowZoom();
                },
            }}
            carousel={
                {
                    finite: slides.filter(p => p.type == "custom-video-slide").length > 0,
                    preload: 2,
                }
            }
            thumbnails={
                {
                    position: "bottom",
                    imageFit: "contain",
                    width: AppImageDimension.ThumbnailWide.width,
                    height: AppImageDimension.ThumbnailWide.height,
                }
            }
            fullscreen={
                {
                    auto: enterFullScreen
                }
            }
            slideshow={
                {
                    ref: slideshowRef,
                    autoplay: autoplay,
                    delay: rotationInterval * 1000
                }
            }
            render={
                {
                    slide: ({ slide, offset }) =>
                        "type" in slide && slide.type === "custom-video-slide"
                            ? <CustomVideoSlideRender slide={slide} offset={offset} onPlaying={onPlaying} />
                            : null,

                    thumbnail: ({ slide, rect, render, imageFit }: { slide: Slide; rect: ContainerRect; render: Render; imageFit: "contain" | "cover"; }) => {
                        var srcUrl = slide.type == "image"
                            ? slide.srcSet?.find(p => p.width <= rect.width)?.src ?? AppImages.EmptyImageThumbnail
                            : slide.type == "custom-video-slide"
                                ? slide.thumbnailUrl!
                                : AppImages.EmptyImageThumbnail;

                        return <img
                            src={srcUrl}
                            style={{ objectFit: AppImageDimension.ThumbnailWide.height > AppImageDimension.ThumbnailWide.width ? "contain" : imageFit }}
                            width={AppImageDimension.ThumbnailWide.width}
                            height={AppImageDimension.ThumbnailWide.height}
                        />;
                    },

                    buttonPrev: count > 1 ? undefined : () => null,
                    buttonNext: count > 1 ? undefined : () => null
                }
            }
            zoom={{
                maxZoomPixelRatio: 1,
                zoomInMultiplier: 2,
                doubleTapDelay: 300,
                doubleClickDelay: 300,
                doubleClickMaxStops: 2,
                keyboardMoveDistance: 50,
                wheelZoomDistanceFactor: 100,
                pinchZoomDistanceFactor: 100,
                scrollToZoom: true
            }}
            plugins={plugins}
        />
    );
};
